import {
  createFeedforwardSignalPayload,
  type FeedforwardAnnotation,
  type FeedforwardRect,
  type FeedforwardSignalOptions,
  type StoryloopFeedforwardPayload,
} from "../../packages/feedback/src/core.js";

const MAX_SCREENSHOT_BYTES = 1_500_000;
const SUCCESS_VISIBLE_MS = 1_600;
const ROOT_ID = "storyloop-feedforward-root";
const STYLE_ID = "storyloop-feedforward-style";
const PIN_LAYER_ID = "storyloop-feedforward-pins";
const SELECT_LAYER_ID = "storyloop-feedforward-selection";
const ANNOTATION_COLORS = [
  { accent: "#c2410c", tint: "rgba(194,65,12,.10)" },
  { accent: "#2563eb", tint: "rgba(37,99,235,.10)" },
  { accent: "#7c3aed", tint: "rgba(124,58,237,.10)" },
  { accent: "#0f766e", tint: "rgba(15,118,110,.10)" },
  { accent: "#be123c", tint: "rgba(190,18,60,.10)" },
  { accent: "#4d7c0f", tint: "rgba(77,124,15,.10)" },
  { accent: "#b45309", tint: "rgba(180,83,9,.10)" },
  { accent: "#0369a1", tint: "rgba(3,105,161,.10)" },
  { accent: "#a21caf", tint: "rgba(162,28,175,.10)" },
  { accent: "#4338ca", tint: "rgba(67,56,202,.10)" },
] as const;
const currentScript = document.currentScript as HTMLScriptElement | null;
let activeSelectionCleanup: (() => void) | null = null;
let activeToneCleanup: (() => void) | null = null;
let activeDismissCleanup: (() => void) | null = null;
let activeTriggerCleanup: (() => void) | null = null;

interface WidgetOptions {
  foreloopKey?: string;
  styleNonce?: string;
  foreloopBaseUrl?: string;
  storyloopFeedforwardKey?: string;
  storyloopFeedforwardBaseUrl?: string;
  storyloopFeedbackKey?: string;
  storyloopApiKey?: string;
  apiKey?: string;
  authMode?: "api-key" | "session";
  tenantId?: string;
  userId?: string;
  project?: string;
  storyloopFeedbackBaseUrl?: string;
  storyloopBaseUrl?: string;
  baseUrl?: string;
  buttonLabel?: string;
  buttonColor?: string;
  buttonColorLight?: string;
  buttonColorDark?: string;
  trigger?: string;
  title?: string;
  companyName?: string;
  placeholder?: string;
  productArea?: string;
  reporterName?: string;
  severity?: string;
  sensitivity?: string;
  includeQuery?: string | boolean;
  includeUrlEvidence?: string | boolean;
  captureScreenshot?: string | boolean;
}

interface WidgetConfig {
  apiKey: string;
  authMode: "api-key" | "session";
  tenantId: string;
  userId: string;
  project: string;
  baseUrl: string;
  buttonLabel: string;
  buttonColorLight: string;
  buttonColorDark: string;
  trigger: string;
  title: string;
  placeholder: string;
  productArea: string;
  reporterName: string;
  severity: string;
  sensitivity: string;
  includeQuery: boolean;
  includeUrlEvidence: boolean;
  captureScreenshot: boolean;
}

interface Screenshot {
  filename: string;
  contentType: "image/jpeg";
  dataBase64: string;
  sizeBytes: number;
}

interface ScreenshotState {
  screenshot: Screenshot | null;
  note: string;
  ref: string;
}

interface SubmissionState {
  sourceId: string;
  signalId: string;
  evidenceRefs: string[];
}

interface SignalResponse {
  subject_id?: string;
}

interface AttachmentResponse {
  id?: string;
  ref?: string;
}

declare global {
  interface Window {
    StoryloopFeedforward?: {
      init(options?: WidgetOptions): void;
    };
    StoryloopFeedback?: {
      init(options?: WidgetOptions): void;
    };
  }
}

function ready(callback: () => void): void {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
  } else {
    callback();
  }
}

function dataset(): DOMStringMap {
  return currentScript?.dataset ?? {};
}

function value(options: WidgetOptions, primary: keyof WidgetOptions, fallback?: keyof WidgetOptions): string {
  const direct = options[primary];
  if (direct !== undefined) return String(direct);
  if (fallback && options[fallback] !== undefined) return String(options[fallback]);
  const data = dataset();
  const fromData = data[primary];
  if (fromData) return fromData;
  return fallback ? data[fallback] ?? "" : "";
}

function defaultBaseUrl(): string {
  if (!currentScript?.src) return `${window.location.origin}/api/storyloop`;
  try {
    return new URL("/api/storyloop", currentScript.src).toString().replace(/\/$/, "");
  } catch {
    return `${window.location.origin}/api/storyloop`;
  }
}

function configFrom(options: WidgetOptions): WidgetConfig {
  const data = dataset();
  const configuredSeverity = value(options, "severity");
  return {
    apiKey:
      value(options, "foreloopKey", "apiKey") ||
      value(options, "storyloopFeedforwardKey", "apiKey") ||
      value(options, "storyloopFeedbackKey", "apiKey") ||
      value(options, "storyloopApiKey", "apiKey"),
    authMode: value(options, "authMode") === "session" ? "session" : "api-key",
    tenantId: value(options, "tenantId"),
    userId: value(options, "userId"),
    project: value(options, "project"),
    baseUrl:
      value(options, "foreloopBaseUrl", "baseUrl") ||
      value(options, "storyloopFeedforwardBaseUrl", "baseUrl") ||
      value(options, "storyloopFeedbackBaseUrl", "baseUrl") ||
      value(options, "storyloopBaseUrl", "baseUrl") ||
      defaultBaseUrl(),
    buttonLabel: value(options, "buttonLabel") || data.label || "Foreloop Feedback",
    // Theming for the two launcher icon variants: the dark icon shows on
    // light pages, the light icon on dark pages. buttonColor sets both.
    buttonColorLight: value(options, "buttonColorLight", "buttonColor"),
    buttonColorDark: value(options, "buttonColorDark", "buttonColor"),
    // A host-provided CSS selector that opens the widget; when set, the
    // floating launcher is hidden and the panel renders as a centered modal.
    trigger: value(options, "trigger"),
    title: value(options, "title") || `Help ${value(options, "companyName") || "us"} help you`,
    placeholder: value(options, "placeholder"),
    productArea: value(options, "productArea"),
    reporterName: value(options, "reporterName"),
    severity: configuredSeverity === "none" ? "" : configuredSeverity,
    sensitivity: value(options, "sensitivity") || "customer_pii",
    includeQuery: value(options, "includeQuery") === "true" || data.includeQuery === "true",
    includeUrlEvidence:
      value(options, "includeUrlEvidence") !== "false" && data.includeUrlEvidence !== "false",
    captureScreenshot:
      value(options, "captureScreenshot") !== "false" && data.captureScreenshot !== "false",
  };
}

function installStyles(nonce: string): void {
  const existing = document.getElementById(STYLE_ID);
  if (existing) {
    if (!nonce || existing.getAttribute("nonce") === nonce) return;
    // CSP judges a style element when it is inserted; changing the nonce on
    // a node the policy already rejected does not resurrect it. Rebuild the
    // element so it enters the document in its authorized state.
    existing.remove();
  }
  const style = document.createElement("style");
  style.id = STYLE_ID;
  // Hosts with a strict style-src pass their CSP nonce (styleNonce option /
  // data-style-nonce attribute) so the injected stylesheet is permitted.
  // Both the IDL property and the attribute are set: browsers key CSP off
  // the internal nonce slot, which either path populates depending on engine.
  if (nonce) {
    style.nonce = nonce;
    style.setAttribute("nonce", nonce);
  }
  style.textContent = [
    ".slfb-root{position:fixed;right:20px;bottom:20px;z-index:2147483000;display:grid;justify-items:end;font:14px/1.4 Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,\"Segoe UI\",sans-serif;color:#f2f4f3}",
    ".slfb-button{display:grid;place-items:center;position:relative;z-index:1;width:42px;height:42px;padding:0;border:0;border-radius:12px;background:var(--slfb-button-dark,#171717);color:var(--slfb-ink-dark,#E9E9E9);overflow:hidden;box-shadow:0 10px 30px rgba(15,23,42,.28);cursor:pointer;transition:transform .15s ease,background .15s ease,color .15s ease}",
    ".slfb-foreloop-mark{display:block;grid-area:1/1;width:100%;height:100%}",
    ".slfb-button:hover{transform:scale(1.06)}",
    ".slfb-root[data-tone=dark] .slfb-button{background:var(--slfb-button-light,#fff);color:var(--slfb-ink-light,#272727);box-shadow:0 10px 30px rgba(0,0,0,.4)}",
    ".slfb-root[data-button-transparent=true] .slfb-button{color:#272727;box-shadow:none}",
    ".slfb-root[data-button-transparent=true][data-tone=dark] .slfb-button{color:#E9E9E9}",
    // While the panel is open the launcher overlaps its dark corner, so every
    // variant gets a solid light chip there for contrast against the panel.
    ".slfb-button[aria-expanded=true]{background:var(--slfb-button-light,#fff);color:var(--slfb-ink-light,#272727);box-shadow:0 10px 30px rgba(0,0,0,.4)}",
    ".slfb-root[data-button-transparent=true] .slfb-button[aria-expanded=true]{background:#fff;color:#272727;box-shadow:0 10px 30px rgba(0,0,0,.4)}",
    // Host-trigger mode: no launcher, panel centered over a dimmed backdrop.
    ".slfb-root[data-mode=modal]{inset:0;display:grid;place-items:center;padding:24px;pointer-events:none;background:rgba(5,8,7,0);transition:background .2s ease}",
    ".slfb-root[data-mode=modal][data-open=true]{pointer-events:auto;background:rgba(5,8,7,.55)}",
    ".slfb-root[data-mode=modal] .slfb-button{display:none}",
    ".slfb-root[data-mode=modal] .slfb-panel{width:min(420px,calc(100vw - 48px));margin:0;max-height:calc(100vh - 64px);transform-origin:center;pointer-events:auto}",
    ".slfb-root[data-mode=modal] .slfb-menu{padding-bottom:14px}",
    ".slfb-root[data-mode=modal] .slfb-actions{padding-bottom:8px}",
    ".slfb-panel{width:min(360px,calc(100vw - 32px));max-height:calc(100vh - 80px);margin:0 15px -27px 0;color-scheme:dark;background:#222;color:#ececec;border:1px solid rgba(255,255,255,.09);border-radius:12px;box-shadow:0 20px 50px rgba(3,8,6,.5);overflow:auto;font-size:13px;transform-origin:100% 100%;transition:transform .24s cubic-bezier(.2,.8,.2,1),opacity .16s ease}",
    "@starting-style{.slfb-panel:not([hidden]){opacity:0;transform:scale(.97) translateY(8px)}}",
    "@media(prefers-reduced-motion:reduce){.slfb-button,.slfb-panel{transition:none}}",
    ".slfb-menu{display:grid;min-width:0;gap:6px;padding:12px 12px 14px}",
    ".slfb-menu[hidden],.slfb-body[hidden],.slfb-actions[hidden],.slfb-section[hidden],.slfb-label[hidden]{display:none}",
    ".slfb-row[data-single=true]{grid-template-columns:minmax(0,1fr)}",
    ".slfb-footer{padding:0 15px 10px;color:rgba(236,236,236,.35);font-size:11px;line-height:14px;font-weight:500}",
    ".slfb-footer-link{color:rgba(236,236,236,.7);text-decoration:underline;text-underline-offset:2px}.slfb-footer-link:hover{color:#ececec}",
    ".slfb-menu-item{display:flex;align-items:center;gap:10px;width:100%;min-height:38px;padding:0 11px;border:1px solid rgba(255,255,255,.08);border-radius:8px;background:rgba(255,255,255,.03);color:#ececec;font:inherit;font-size:13px;font-weight:500;text-align:left;cursor:pointer;transition:background .12s ease,border-color .12s ease}",
    ".slfb-menu-item:hover{background:rgba(255,255,255,.06);border-color:rgba(255,255,255,.18)}",
    ".slfb-menu-icon{display:block;flex:none;width:16px;height:16px;color:#9a9a9a}",
    ".slfb-head,.slfb-actions{display:flex;min-width:0;align-items:center;justify-content:space-between;gap:12px}",
    ".slfb-head{padding:12px 15px 2px}",
    ".slfb-title{font-weight:600;font-size:14px;color:#ececec}",
    ".slfb-ai-dot{display:inline-block;width:8px;height:8px;margin-right:9px;border-radius:50%;background:#3fd68f;vertical-align:1px;animation:slfb-pulse 2s ease-in-out infinite}",
    "@keyframes slfb-pulse{0%,100%{box-shadow:0 0 0 0 rgba(63,214,143,.55)}70%{box-shadow:0 0 0 6px rgba(63,214,143,0)}}",
    "@media(prefers-reduced-motion:reduce){.slfb-ai-dot{animation:none}}",
    ".slfb-close,.slfb-capture{border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.04);color:#ececec;min-height:28px;padding:0 10px;border-radius:7px;cursor:pointer;font:inherit;font-size:12px}",
    ".slfb-show-where{flex:0 0 auto;white-space:nowrap;border:1px solid rgba(255,255,255,.16);background:transparent;color:#ececec;min-height:28px;padding:0 10px;border-radius:7px;cursor:pointer;font:inherit;font-size:12px;font-weight:500}",
    ".slfb-show-where:hover,.slfb-capture:hover{border-color:rgba(255,255,255,.3)}",
    ".slfb-close{width:28px;padding:0;color:#9a9a9a}",
    ".slfb-body{display:grid;min-width:0;gap:10px;padding:14px}",
    ".slfb-label{display:grid;gap:5px;color:#9a9a9a;font-size:12px;font-weight:500}",
    ".slfb-textarea,.slfb-input,.slfb-select{box-sizing:border-box;width:100%;border:1px solid rgba(255,255,255,.1);border-radius:7px;background:#101010;color:#ececec;font:inherit;font-size:13px;padding:8px 9px}",
    ".slfb-textarea:focus,.slfb-input:focus,.slfb-select:focus{border-color:rgba(255,255,255,.28);outline:1px solid rgba(255,255,255,.14);outline-offset:0}",
    ".slfb-textarea::placeholder,.slfb-input::placeholder{color:rgba(236,236,236,.35)}",
    ".slfb-textarea{min-height:100px;resize:vertical}",
    ".slfb-row{display:grid;min-width:0;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:10px}",
    ".slfb-section{display:grid;min-width:0;gap:6px;justify-items:start}",
    ".slfb-section-label{color:#9a9a9a;font-size:12px;font-weight:500}",
    ".slfb-required{color:#ff9d94;margin-left:2px}",
    ".slfb-capture{white-space:nowrap}",
    ".slfb-annotations{display:grid;min-width:0;justify-self:stretch;gap:8px}.slfb-annotations:empty{display:none}",
    ".slfb-annotation{display:grid;min-width:0;grid-template-columns:minmax(0,1fr) auto;gap:6px;padding:8px;border:1px solid var(--slfb-accent);border-radius:8px;background:rgba(255,255,255,.02)}",
    ".slfb-annotation-summary{min-width:0;align-self:center}",
    ".slfb-annotation-title{display:flex;min-width:0;align-items:center;gap:6px;font-size:12px;font-weight:600}.slfb-annotation-number{display:grid;flex:0 0 auto;place-items:center;width:20px;height:20px;border-radius:50%;background:var(--slfb-accent);color:#fff;font:600 11px/1 system-ui,sans-serif}.slfb-annotation-label{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}",
    ".slfb-annotation-note{grid-column:1/-1}.slfb-annotation-note:focus{border-color:var(--slfb-accent);outline:2px solid var(--slfb-tint);outline-offset:1px}",
    ".slfb-annotation-remove{display:grid;place-items:center;align-self:start;width:24px;height:24px;padding:0;border:0;border-radius:6px;background:transparent;color:#9a9a9a;cursor:pointer;transition:background .12s ease,color .12s ease}",
    ".slfb-annotation-remove:hover{background:rgba(255,255,255,.08);color:#ececec}",
    ".slfb-annotation-remove-icon{display:block;width:14px;height:14px}",
    ".slfb-selection-banner{position:fixed;top:16px;left:50%;z-index:2147483001;transform:translateX(-50%);padding:9px 13px;border:2px solid var(--slfb-accent);border-radius:999px;background:#17201d;color:#fff;box-shadow:0 8px 26px rgba(15,23,42,.24);font:600 13px/1.3 Inter,ui-sans-serif,system-ui,sans-serif;pointer-events:none}",
    ".slfb-selection-box,.slfb-pin{position:fixed;box-sizing:border-box;border:2px solid var(--slfb-accent);background:var(--slfb-tint);pointer-events:none}",
    ".slfb-selection-box{z-index:2147482999}.slfb-pins{position:fixed;inset:0;z-index:2147482998;pointer-events:none}",
    ".slfb-pin{z-index:1}.slfb-pin-label{position:absolute;top:-11px;left:-11px;display:grid;place-items:center;width:20px;height:20px;border-radius:50%;background:var(--slfb-accent);color:#fff;font:700 11px/1 system-ui,sans-serif;box-shadow:0 2px 6px rgba(15,23,42,.25)}",
    ".slfb-shot-note,.slfb-status{color:#9a9a9a;font-size:12px}.slfb-shot-note:empty{display:none}",
    ".slfb-shot-preview{display:none;width:100%;max-height:120px;object-fit:cover;border:1px solid rgba(255,255,255,.1);border-radius:7px;background:#101010}",
    ".slfb-shot-preview[data-visible=true]{display:block}",
    ".slfb-actions{padding:10px 39px 2px 14px;border-top:1px solid rgba(255,255,255,.08)}",
    ".slfb-action-buttons{display:flex;flex:0 0 auto;gap:7px}.slfb-cancel{border:1px solid rgba(255,255,255,.16);background:transparent;color:#ececec;min-height:32px;padding:0 12px;border-radius:7px;cursor:pointer;font:inherit;font-size:12.5px;font-weight:500}",
    ".slfb-cancel:hover{border-color:rgba(255,255,255,.3)}",
    ".slfb-submit{border:0;background:#fff;color:#141414;min-height:32px;min-width:78px;padding:0 22px;border-radius:7px;cursor:pointer;font:inherit;font-size:12.5px;font-weight:600}",
    ".slfb-submit:hover{background:#e4e4e4}",
    ".slfb-status{min-height:17px}.slfb-status[data-tone=error]{color:#ff9d94}.slfb-status[data-tone=success]{color:#c9cfcc}",
    ".slfb-success{display:grid;justify-items:center;gap:7px;padding:30px 20px 22px;text-align:center}.slfb-success[hidden]{display:none}.slfb-success-icon{display:grid;place-items:center;width:38px;height:38px;border-radius:10px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.18);color:#ececec;font:600 19px/1 system-ui,sans-serif}.slfb-success-title{font-size:14px;font-weight:600}.slfb-success-copy{max-width:260px;color:#9a9a9a;font-size:12px}",
    ".slfb-submit:disabled,.slfb-capture:disabled{opacity:.55;cursor:not-allowed}",
    "@media(max-width:520px){.slfb-root{right:12px;bottom:12px}.slfb-row{grid-template-columns:1fr}}",
  ].join("");
  document.head.appendChild(style);
}

function selectedText(): string {
  const selection = window.getSelection ? String(window.getSelection() ?? "").trim() : "";
  return selection.length > 500 ? `${selection.slice(0, 500)}...` : selection;
}

function pageOptions(
  config: WidgetConfig,
  comment: string,
  reporterName: string,
  severity: string,
  screenshotState: ScreenshotState,
  sourceId: string,
  annotations: FeedforwardAnnotation[],
  category: string,
): FeedforwardSignalOptions {
  return {
    comment,
    category,
    project: config.project,
    url: window.location.href,
    includeQuery: config.includeQuery,
    pageTitle: document.title || "Not captured",
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    userAgent: window.navigator.userAgent,
    selectedText: selectedText(),
    productArea: config.productArea,
    ...(screenshotState.ref ? { screenshotRef: screenshotState.ref } : {}),
    screenshotNote: screenshotState.screenshot
      ? "Captured locally; upload pending."
      : screenshotState.note,
    annotations,
    sourceId,
    reporterName,
    severity,
    sensitivity: config.sensitivity,
  };
}

function uniqueStrings(values: string[]): string[] {
  return [...new Set(values.map((item) => item.trim()).filter(Boolean))];
}

function uniqueSourceId(): string {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return `feedforward-widget:${window.crypto.randomUUID()}`;
  }
  return `feedforward-widget:${Date.now()}:${Math.random().toString(36).slice(2)}`;
}

function errorMessage(error: unknown): string {
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  return "Unknown error";
}

function roundedRect(rect: DOMRect | ClientRect): FeedforwardRect {
  return {
    x: Math.round(rect.x),
    y: Math.round(rect.y),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
}

function clippedText(value: string, limit = 300): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > limit ? `${normalized.slice(0, limit - 3)}...` : normalized;
}

function attributeSelector(name: string, value: string): string {
  return `[${name}="${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"]`;
}

function cssIdentifier(value: string): string {
  if (window.CSS?.escape) return window.CSS.escape(value);
  return value.replace(/[^a-zA-Z0-9_-]/g, (character) => `\\${character}`);
}

function stableSelector(target: Element): string {
  for (const name of ["data-testid", "data-test", "data-cy"]) {
    const value = target.getAttribute(name)?.trim();
    const selector = value ? attributeSelector(name, clippedText(value, 160)) : "";
    if (selector && selectorCount(selector) === 1) return selector;
  }
  if (target.id) return `#${cssIdentifier(target.id)}`;
  const parts: string[] = [];
  let current: Element | null = target;
  while (current && current !== document.documentElement && parts.length < 5) {
    let part = current.tagName.toLowerCase();
    const role = current.getAttribute("role")?.trim();
    const ariaLabel = current.getAttribute("aria-label")?.trim();
    if (role) part += attributeSelector("role", clippedText(role, 80));
    else if (ariaLabel) part += attributeSelector("aria-label", clippedText(ariaLabel, 120));
    else if (current.parentElement) {
      const siblings = [...current.parentElement.children].filter((item) => item.tagName === current?.tagName);
      if (siblings.length > 1) part += `:nth-of-type(${siblings.indexOf(current) + 1})`;
    }
    parts.unshift(part);
    if (selectorCount(parts.join(" > ")) === 1) return parts.join(" > ");
    current = current.parentElement;
  }
  return parts.join(" > ") || target.tagName.toLowerCase();
}

function selectorCount(selector: string): number {
  try {
    return document.querySelectorAll(selector).length;
  } catch {
    return 0;
  }
}

function isPrivateTarget(target: Element): boolean {
  const sensitive = 'input,textarea,select,[contenteditable="true"],[data-private],[data-sensitive],[data-foreloop-private],[data-storyloop-private]';
  return Boolean(target.closest(sensitive) || target.querySelector(sensitive));
}

function elementLabel(target: Element): string {
  const ariaLabel = target.getAttribute("aria-label")?.trim();
  if (ariaLabel) return clippedText(ariaLabel, 120);
  if (!isPrivateTarget(target)) {
    const visibleText = clippedText(target.textContent ?? "", 120);
    if (visibleText) return visibleText;
  }
  const role = target.getAttribute("role")?.trim();
  return role ? `${role} (${target.tagName.toLowerCase()})` : target.tagName.toLowerCase();
}

function elementAnnotation(target: Element): FeedforwardAnnotation {
  const text = isPrivateTarget(target) ? "" : clippedText(target.textContent ?? "", 300);
  return {
    type: "element",
    selector: stableSelector(target),
    label: elementLabel(target),
    ...(text ? { text } : {}),
    rects: [roundedRect(target.getBoundingClientRect())],
  };
}

function textAnnotation(selection: Selection): FeedforwardAnnotation | null {
  const text = clippedText(String(selection), 500);
  if (!text || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  const container = range.commonAncestorContainer.nodeType === 1
    ? range.commonAncestorContainer as Element
    : range.commonAncestorContainer.parentElement;
  if (!container || isPrivateTarget(container) || container.closest(`#${ROOT_ID}`)) return null;
  const context = clippedText(container.textContent ?? "", 700);
  const rects = [...range.getClientRects()]
    .filter((rect) => rect.width > 0 && rect.height > 0)
    .slice(0, 12)
    .map(roundedRect);
  return {
    type: "text",
    selector: stableSelector(container),
    label: text,
    text,
    ...(context && context !== text ? { context } : {}),
    rects: rects.length > 0 ? rects : [roundedRect(range.getBoundingClientRect())],
  };
}

function positionBox(node: HTMLElement, rect: FeedforwardRect): void {
  node.style.left = `${rect.x}px`;
  node.style.top = `${rect.y}px`;
  node.style.width = `${Math.max(1, rect.width)}px`;
  node.style.height = `${Math.max(1, rect.height)}px`;
}

function applyAnnotationColor(node: HTMLElement, index: number): void {
  const color = ANNOTATION_COLORS[index % ANNOTATION_COLORS.length]!;
  node.style.setProperty("--slfb-accent", color.accent);
  node.style.setProperty("--slfb-tint", color.tint);
}

function renderPins(
  annotations: FeedforwardAnnotation[],
  colorIndexes: ReadonlyMap<FeedforwardAnnotation, number>,
): void {
  document.getElementById(PIN_LAYER_ID)?.remove();
  if (annotations.length === 0) return;
  const layer = element("div", "slfb-pins");
  layer.id = PIN_LAYER_ID;
  annotations.forEach((annotation, index) => {
    annotation.rects.forEach((rect, rectIndex) => {
      const pin = element("div", "slfb-pin");
      applyAnnotationColor(pin, colorIndexes.get(annotation) ?? index);
      positionBox(pin, rect);
      if (rectIndex === 0) pin.append(element("span", "slfb-pin-label", String(index + 1)));
      layer.append(pin);
    });
  });
  document.body.append(layer);
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Browser could not encode the screenshot."))),
      "image/jpeg",
      quality,
    );
  });
}

async function frameBlob(video: HTMLVideoElement, maxWidth: number, quality: number): Promise<Blob> {
  if (!video.videoWidth || !video.videoHeight) {
    throw new Error("Browser returned an empty screenshot stream.");
  }
  const scale = Math.min(1, maxWidth / video.videoWidth);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
  canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Browser could not prepare the screenshot canvas.");
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvasToBlob(canvas, quality);
}

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return window.btoa(binary);
}

async function captureScreenshot(root: HTMLElement): Promise<Screenshot> {
  if (!window.navigator.mediaDevices?.getDisplayMedia) {
    throw new Error("Browser does not support screen capture.");
  }
  const previousVisibility = root.style.visibility;
  root.style.visibility = "hidden";
  let stream: MediaStream | null = null;
  try {
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
    stream = await window.navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error("Browser could not load the screenshot stream."));
    });
    await video.play();
    let blob = await frameBlob(video, 1280, 0.68);
    if (blob.size > MAX_SCREENSHOT_BYTES) blob = await frameBlob(video, 960, 0.55);
    if (blob.size > MAX_SCREENSHOT_BYTES) {
      throw new Error("Screenshot exceeds the 1.5MB upload limit after compression.");
    }
    return {
      filename: `storyloop-feedforward-${new Date().toISOString().replace(/[:.]/g, "-")}.jpg`,
      contentType: "image/jpeg",
      dataBase64: await blobToBase64(blob),
      sizeBytes: blob.size,
    };
  } finally {
    stream?.getTracks().forEach((track) => track.stop());
    root.style.visibility = previousVisibility;
  }
}

async function requestJson<T>(
  url: string,
  config: WidgetConfig,
  method: "POST" | "PATCH",
  body: unknown,
): Promise<T> {
  let response: Response;
  try {
    response = await window.fetch(url, {
      method,
      mode: "cors",
      credentials: config.authMode === "session" ? "same-origin" : "omit",
      headers: {
        accept: "application/json",
        ...(config.authMode === "api-key" ? { authorization: `Bearer ${config.apiKey}` } : {}),
        ...(config.authMode === "session" && config.tenantId
          ? { "x-storyloop-tenant-id": config.tenantId }
          : {}),
        ...(config.authMode === "session" && config.userId
          ? { "x-storyloop-user-id": config.userId }
          : {}),
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error("Storyloop request failed: network error.");
  }
  if (!response.ok) throw new Error(`Storyloop returned ${response.status}`);
  return response.json() as Promise<T>;
}

async function uploadAttachment(
  config: WidgetConfig,
  signalId: string,
  screenshot: Screenshot,
): Promise<AttachmentResponse> {
  return requestJson(
    `${signalBaseUrl(config)}/${encodeURIComponent(signalId)}/attachments`,
    config,
    "POST",
    {
      filename: screenshot.filename,
      content_type: screenshot.contentType,
      data_base64: screenshot.dataBase64,
    },
  );
}

async function patchScreenshot(
  config: WidgetConfig,
  signalId: string,
  payload: StoryloopFeedforwardPayload,
): Promise<void> {
  await requestJson(
    `${signalBaseUrl(config)}/${encodeURIComponent(signalId)}`,
    config,
    "PATCH",
    { body_markdown: payload.body_markdown, evidence_refs: payload.evidence_refs },
  );
}

function createPanel(config: WidgetConfig, root: HTMLElement): void {
  activeSelectionCleanup?.();
  activeSelectionCleanup = null;
  activeToneCleanup?.();
  activeToneCleanup = null;
  activeDismissCleanup?.();
  activeDismissCleanup = null;
  activeTriggerCleanup?.();
  activeTriggerCleanup = null;
  document.getElementById(PIN_LAYER_ID)?.remove();
  document.getElementById(SELECT_LAYER_ID)?.remove();
  root.replaceChildren();
  // The rebuilt panel starts hidden; clear any open-state left by a previous
  // init or a modal backdrop would linger over the page with no dialog.
  root.dataset.open = "false";
  let screenshotState: ScreenshotState = { screenshot: null, note: "Not captured.", ref: "" };
  let submissionState: SubmissionState | null = null;
  let annotations: FeedforwardAnnotation[] = [];
  const annotationColorIndexes = new Map<FeedforwardAnnotation, number>();
  let nextAnnotationColor = 0;
  let successVisible = false;
  let successTimer: number | null = null;
  let category = "";

  const panel = element("section", "slfb-panel");
  panel.hidden = true;
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "false");
  panel.setAttribute("aria-label", "Storyloop Feedforward");
  const head = element("div", "slfb-head");
  const title = element("div", "slfb-title");
  const close = element("button", "slfb-close", "×") as HTMLButtonElement;
  close.type = "button";
  close.setAttribute("aria-label", "Close Feedforward");
  head.append(title, close);

  const form = document.createElement("form");
  const body = element("div", "slfb-body");
  const comment = document.createElement("textarea");
  comment.className = "slfb-textarea";
  comment.name = "comment";
  comment.required = true;
  if (config.placeholder) comment.placeholder = config.placeholder;
  comment.setAttribute("aria-label", "What can be better?");
  const requiredMark = element("span", "slfb-required", "*");
  requiredMark.setAttribute("aria-hidden", "true");

  const annotationActions = element("div", "slfb-section");
  const showWhere = element("button", "slfb-show-where", "Add annotation") as HTMLButtonElement;
  showWhere.type = "button";
  const annotationsBox = element("div", "slfb-annotations");
  annotationActions.append(
    element("div", "slfb-section-label", "Highlight the issues (optional)"),
    annotationsBox,
    showWhere,
  );

  const reporter = document.createElement("input");
  reporter.className = "slfb-input";
  reporter.name = "reporter";
  reporter.autocomplete = "email";
  reporter.inputMode = "email";
  reporter.placeholder = "Optional";
  reporter.value = config.reporterName;
  const severity = document.createElement("select");
  severity.className = "slfb-select";
  severity.name = "severity";
  const priorityOptions: Array<[value: string, label: string]> = [
    ["", "Select priority"],
    ["low", "Low"],
    ["medium", "Medium"],
    ["high", "High"],
    ["critical", "Critical"],
  ];
  for (const [value, label] of priorityOptions) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = label;
    severity.appendChild(option);
  }
  severity.value = config.severity;
  const priorityField = labeled("Priority", severity);
  const row = element("div", "slfb-row");
  row.append(priorityField, labeled("Email", reporter));

  const screenshotBox = element("div", "slfb-section");
  const capture = element("button", "slfb-capture", "Select capture area") as HTMLButtonElement;
  capture.type = "button";
  const screenshotNote = element("div", "slfb-shot-note");
  const preview = element("img", "slfb-shot-preview") as HTMLImageElement;
  preview.alt = "Captured screenshot preview";
  screenshotBox.append(
    element("div", "slfb-section-label", "Share screenshot (optional)"),
    capture,
    screenshotNote,
    preview,
  );
  body.append(
    comment,
    annotationActions,
    ...(config.captureScreenshot ? [screenshotBox] : []),
    row,
  );

  const actions = element("div", "slfb-actions");
  const status = element("div", "slfb-status");
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  const submit = element("button", "slfb-submit", "Send") as HTMLButtonElement;
  submit.type = "submit";
  const cancel = element("button", "slfb-cancel", "Cancel") as HTMLButtonElement;
  cancel.type = "button";
  const actionButtons = element("div", "slfb-action-buttons");
  actionButtons.append(cancel, submit);
  actions.append(status, actionButtons);
  const success = element("div", "slfb-success");
  success.hidden = true;
  success.tabIndex = -1;
  success.setAttribute("role", "status");
  success.setAttribute("aria-live", "polite");
  const successIcon = element("div", "slfb-success-icon", "✓");
  successIcon.setAttribute("aria-hidden", "true");
  success.append(
    successIcon,
    element("div", "slfb-success-title", "Sent to Foreloop"),
    element("div", "slfb-success-copy", "Thanks — your feedback is on its way."),
  );
  form.append(body, actions, success);
  const toggle = element("button", "slfb-button") as HTMLButtonElement;
  toggle.type = "button";
  toggle.append(foreloopMark());
  // With a transparent chip the glyph sits directly on the page, so the ink
  // tracks the page tone instead of contrasting with the chip.
  if (config.buttonColorDark === "transparent" || config.buttonColorLight === "transparent") {
    root.dataset.buttonTransparent = "true";
  } else {
    delete root.dataset.buttonTransparent;
  }
  // Themed chips derive contrasting ink from the color's luminance so a dark
  // buttonColor never meets the default dark ink (and vice versa). Colors the
  // parser can't read (e.g. named colors) keep the default ink.
  const applyButtonColor = (side: "dark" | "light", color: string): void => {
    if (!color) {
      root.style.removeProperty(`--slfb-button-${side}`);
      root.style.removeProperty(`--slfb-ink-${side}`);
      return;
    }
    root.style.setProperty(`--slfb-button-${side}`, color);
    const parsed = parseCssColor(color);
    if (parsed && parsed.a >= 0.5) {
      root.style.setProperty(`--slfb-ink-${side}`, relativeLuminance(parsed) < 0.45 ? "#E9E9E9" : "#272727");
    } else {
      root.style.removeProperty(`--slfb-ink-${side}`);
    }
  };
  applyButtonColor("dark", config.buttonColorDark);
  applyButtonColor("light", config.buttonColorLight);
  toggle.setAttribute("aria-label", config.buttonLabel);
  toggle.setAttribute("aria-expanded", "false");
  toggle.setAttribute("aria-haspopup", "dialog");

  const menu = element("div", "slfb-menu");
  // Each category configures its own heading and which optional sections the
  // shared form shows; `available: false` keeps a category wired but unlisted.
  interface MenuChoice {
    label: string;
    heading: string;
    priority: boolean;
    annotate: boolean;
    screenshot: boolean;
    available: boolean;
    icon: string[];
  }
  const menuChoices: MenuChoice[] = [
    {
      label: "Automatic bug fix",
      heading: "What's broken?",
      priority: true,
      annotate: true,
      screenshot: true,
      available: true,
      icon: [
        "m8 2 1.88 1.88",
        "M14.12 3.88 16 2",
        "M9 7.13v-1a3.003 3.003 0 1 1 6 0v1",
        "M18 11a4 4 0 0 0-4-4h-4a4 4 0 0 0-4 4v3a6 6 0 0 0 12 0z",
        "M12 20v-9",
        "M6.53 9C4.6 8.8 3 7.1 3 5",
        "M6 13H2",
        "M3 21c0-2.1 1.7-3.9 3.8-4",
        "M20.97 5c0 2.1-1.6 3.8-3.5 4",
        "M22 13h-4",
        "M17.2 17c2.1.1 3.8 1.9 3.8 4",
      ],
    },
    {
      label: "Automatic product improvement",
      heading: "What can be better?",
      priority: false,
      annotate: true,
      screenshot: true,
      available: true,
      icon: [
        "M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5",
        "M9 18h6",
        "M10 22h4",
      ],
    },
    {
      // Wired but not yet listed — support launches soon.
      label: "Get support",
      heading: "How can we help?",
      priority: false,
      annotate: false,
      screenshot: true,
      available: false,
      icon: [
        "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20",
        "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8",
        "m4.93 4.93 4.24 4.24",
        "m14.83 9.17 4.24-4.24",
        "m14.83 14.83 4.24 4.24",
        "m9.17 14.83-4.24 4.24",
      ],
    },
  ];
  let activeChoice: MenuChoice | null = null;
  const menuItems = menuChoices
    .filter((choice) => choice.available)
    .map((choice) => {
      const item = element("button", "slfb-menu-item") as HTMLButtonElement;
      item.type = "button";
      item.append(menuIcon(choice.icon), document.createTextNode(choice.label));
      item.addEventListener("click", () => {
        category = choice.label;
        activeChoice = choice;
        comment.setAttribute("aria-label", choice.heading);
        priorityField.hidden = !choice.priority;
        row.dataset.single = String(!choice.priority);
        annotationActions.hidden = !choice.annotate;
        screenshotBox.hidden = !choice.screenshot;
        syncViews();
        comment.focus();
      });
      return item;
    });
  menu.append(...menuItems);
  form.hidden = true;
  const footer = element("div", "slfb-footer", "Powered by ");
  const footerLink = document.createElement("a");
  footerLink.className = "slfb-footer-link";
  footerLink.textContent = "Foreloop";
  footerLink.href = "https://foreloop.com";
  footerLink.target = "_blank";
  footerLink.rel = "noopener noreferrer";
  footer.append(footerLink);
  panel.append(head, menu, form, footer);

  const statusDot = element("span", "slfb-ai-dot");
  statusDot.setAttribute("aria-hidden", "true");
  function showMenuTitle(): void {
    title.replaceChildren(statusDot, "Foreloop AI online");
  }
  showMenuTitle();

  function syncViews(): void {
    menu.hidden = Boolean(category);
    form.hidden = !category;
    // The head row doubles as the form's heading, so the close button and
    // heading share one flow row — no absolute positioning to keep aligned.
    if (activeChoice) title.replaceChildren(activeChoice.heading, requiredMark);
    else showMenuTitle();
  }

  function setOpen(open: boolean, restoreLauncherFocus = true): void {
    panel.hidden = !open;
    root.dataset.open = String(open);
    toggle.setAttribute("aria-expanded", String(open));
    renderPins(open ? annotations : [], annotationColorIndexes);
    if (open) {
      status.textContent = "";
      syncViews();
      if (category) comment.focus();
      else menuItems[0]?.focus();
    } else if (restoreLauncherFocus) {
      if (config.trigger) lastTrigger?.focus();
      else toggle.focus();
    }
  }

  function reset(): void {
    if (successTimer !== null) window.clearTimeout(successTimer);
    successTimer = null;
    successVisible = false;
    body.hidden = false;
    actions.hidden = false;
    success.hidden = true;
    comment.value = "";
    category = "";
    activeChoice = null;
    reporter.value = config.reporterName;
    severity.value = config.severity;
    screenshotState = { screenshot: null, note: "Not captured.", ref: "" };
    submissionState = null;
    annotations = [];
    annotationColorIndexes.clear();
    nextAnnotationColor = 0;
    renderAnnotationList();
    renderPins([], annotationColorIndexes);
    screenshotNote.textContent = "";
    preview.removeAttribute("src");
    preview.dataset.visible = "false";
    capture.textContent = "Select capture area";
    submit.textContent = "Send";
    status.dataset.tone = "";
    status.textContent = "";
    for (const field of [comment, reporter, severity, capture]) field.disabled = false;
  }

  function showSuccess(): void {
    renderPins([], annotationColorIndexes);
    body.hidden = true;
    actions.hidden = true;
    success.hidden = false;
    successVisible = true;
    success.focus();
    successTimer = window.setTimeout(() => {
      successTimer = null;
      const restoreLauncherFocus = panel.contains(document.activeElement);
      reset();
      setOpen(false, restoreLauncherFocus);
    }, SUCCESS_VISIBLE_MS);
  }

  function finishSuccess(): boolean {
    if (!successVisible) return false;
    reset();
    setOpen(false);
    return true;
  }

  function renderAnnotationList(): void {
    annotationsBox.replaceChildren();
    showWhere.textContent = "Add annotation";
    showWhere.disabled = annotations.length >= 10;
    if (showWhere.disabled) showWhere.textContent = "10 annotations added";
    annotations.forEach((annotation, index) => {
      const card = element("div", "slfb-annotation");
      applyAnnotationColor(card, annotationColorIndexes.get(annotation) ?? index);
      const summary = element("div", "slfb-annotation-summary");
      const annotationTitle = element("div", "slfb-annotation-title");
      const annotationLabel = element("span", "slfb-annotation-label", annotation.label);
      // The selector still travels in the payload; surface it only as a tooltip.
      annotationLabel.title = annotation.selector;
      annotationTitle.append(
        element("span", "slfb-annotation-number", String(index + 1)),
        annotationLabel,
      );
      summary.append(annotationTitle);
      const remove = element("button", "slfb-annotation-remove") as HTMLButtonElement;
      remove.type = "button";
      const trash = menuIcon([
        "M3 6h18",
        "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6",
        "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2",
        "M10 11v6",
        "M14 11v6",
      ]);
      trash.setAttribute("class", "slfb-annotation-remove-icon");
      remove.append(trash);
      remove.setAttribute("aria-label", `Remove annotation ${index + 1}`);
      remove.addEventListener("click", () => {
        annotations.splice(index, 1);
        annotationColorIndexes.delete(annotation);
        renderAnnotationList();
        renderPins(annotations, annotationColorIndexes);
      });
      const note = document.createElement("input");
      note.className = "slfb-input slfb-annotation-note";
      note.placeholder = "What should change here? (optional)";
      note.setAttribute("aria-label", `Note for annotation ${index + 1}`);
      note.value = annotation.note ?? "";
      note.addEventListener("input", () => {
        annotation.note = note.value;
      });
      card.append(summary, remove, note);
      annotationsBox.append(card);
    });
  }

  function beginSelection(): void {
    if (activeSelectionCleanup || annotations.length >= 10) return;
    panel.hidden = true;
    // Also drop the modal backdrop, or it would dim the page and swallow the
    // element-selection clicks. Reopening via setOpen restores it.
    root.dataset.open = "false";
    toggle.hidden = true;
    window.getSelection()?.removeAllRanges?.();
    renderPins(annotations, annotationColorIndexes);
    const layer = element("div", "slfb-selection-layer");
    layer.id = SELECT_LAYER_ID;
    const banner = element(
      "div",
      "slfb-selection-banner",
      "Click an element or select text · Esc to cancel",
    );
    const selectionBox = element("div", "slfb-selection-box");
    applyAnnotationColor(layer, nextAnnotationColor);
    selectionBox.hidden = true;
    layer.append(banner, selectionBox);
    document.body.append(layer);
    const previousCursor = document.documentElement.style.cursor;
    document.documentElement.style.cursor = "crosshair";
    let candidate: Element | null = null;

    const cleanup = (reopen = true) => {
      document.removeEventListener("mousemove", onMove, true);
      document.removeEventListener("mouseup", onMouseUp, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.documentElement.style.cursor = previousCursor;
      layer.remove();
      toggle.hidden = false;
      activeSelectionCleanup = null;
      renderAnnotationList();
      if (reopen) setOpen(true);
    };

    const addAnnotation = (annotation: FeedforwardAnnotation) => {
      annotationColorIndexes.set(annotation, nextAnnotationColor);
      nextAnnotationColor += 1;
      annotations.push(annotation);
      cleanup(true);
    };

    const onMove = (event: MouseEvent) => {
      const target = document.elementFromPoint(event.clientX, event.clientY);
      candidate = target && !target.closest(`#${ROOT_ID},#${SELECT_LAYER_ID},#${PIN_LAYER_ID}`)
        ? target
        : null;
      if (!candidate) {
        selectionBox.hidden = true;
        return;
      }
      positionBox(selectionBox, roundedRect(candidate.getBoundingClientRect()));
      selectionBox.hidden = false;
    };

    const onMouseUp = (event: MouseEvent) => {
      const annotation = window.getSelection ? textAnnotation(window.getSelection()!) : null;
      if (!annotation) return;
      event.preventDefault();
      event.stopPropagation();
      addAnnotation(annotation);
    };

    const onClick = (event: MouseEvent) => {
      if (!candidate) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addAnnotation(elementAnnotation(candidate));
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      cleanup(true);
    };

    document.addEventListener("mousemove", onMove, true);
    document.addEventListener("mouseup", onMouseUp, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKeyDown, true);
    activeSelectionCleanup = () => cleanup(false);
  }

  toggle.addEventListener("click", () => {
    if (!finishSuccess()) setOpen(panel.hidden);
  });
  close.addEventListener("click", () => {
    if (!finishSuccess()) setOpen(false);
  });
  cancel.addEventListener("click", () => {
    reset();
    setOpen(false);
  });
  panel.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !finishSuccess()) setOpen(false);
  });
  // In modal mode the backdrop hides the page, so Tab must wrap within the
  // dialog instead of escaping onto obscured host controls. The corner panel
  // is non-modal, where tabbing out is the correct behavior.
  panel.addEventListener("keydown", (event) => {
    if (event.key !== "Tab" || !config.trigger || event.defaultPrevented) return;
    const focusables = [
      ...panel.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ].filter(
      (el) => !el.closest("[hidden]") && !(el as HTMLButtonElement | HTMLInputElement).disabled,
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
  reporter.addEventListener("keydown", (event) => {
    if (event.key !== "Tab" || event.shiftKey) return;
    event.preventDefault();
    cancel.focus();
  });
  cancel.addEventListener("keydown", (event) => {
    if (event.key !== "Tab") return;
    event.preventDefault();
    if (event.shiftKey) reporter.focus();
    else submit.focus();
  });
  submit.addEventListener("keydown", (event) => {
    if (event.key !== "Tab" || !event.shiftKey) return;
    event.preventDefault();
    cancel.focus();
  });
  showWhere.addEventListener("click", beginSelection);
  capture.addEventListener("click", async () => {
    capture.disabled = true;
    screenshotNote.textContent = "Choose a screen or browser tab to capture...";
    try {
      const screenshot = await captureScreenshot(root);
      screenshotState = { screenshot, note: "", ref: "" };
      screenshotNote.textContent = "Screenshot captured. It will be attached when you send.";
      preview.src = `data:${screenshot.contentType};base64,${screenshot.dataBase64}`;
      preview.dataset.visible = "true";
      capture.textContent = "Reselect capture area";
    } catch (error) {
      screenshotState = { screenshot: null, note: `Not captured: ${errorMessage(error)}`, ref: "" };
      screenshotNote.textContent = screenshotState.note;
      preview.removeAttribute("src");
      preview.dataset.visible = "false";
    } finally {
      capture.disabled = false;
    }
  });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const text = comment.value.trim();
    if (!text) {
      comment.focus();
      return;
    }
    if (config.authMode === "api-key" && !config.apiKey) {
      status.dataset.tone = "error";
      status.textContent = "Feedforward key missing.";
      return;
    }
    submit.disabled = true;
    status.dataset.tone = "";
    status.textContent = submissionState ? "Retrying screenshot..." : "Sending...";
    submit.textContent = "Send";
    const sourceId = submissionState?.sourceId || uniqueSourceId();
    try {
      let payload = createFeedforwardSignalPayload(
        pageOptions(config, text, reporter.value.trim(), severity.value, screenshotState, sourceId, annotations, category),
      );
      if (!config.includeUrlEvidence && !screenshotState.ref) payload.evidence_refs = [];
      if (!submissionState) {
        const signal = await requestJson<SignalResponse>(
          signalBaseUrl(config),
          config,
          "POST",
          payload,
        );
        submissionState = {
          sourceId,
          signalId: signal.subject_id || "",
          evidenceRefs: payload.evidence_refs,
        };
      }
      if (screenshotState.screenshot) {
        if (!submissionState.signalId) {
          throw new Error("Feedforward sent, but Storyloop did not return an attachment target.");
        }
        for (const field of [comment, reporter, severity, capture]) field.disabled = true;
        if (!screenshotState.ref) {
          status.textContent = "Uploading screenshot...";
          const attachment = await uploadAttachment(
            config,
            submissionState.signalId,
            screenshotState.screenshot,
          );
          screenshotState.ref = attachment.ref || `storyloop-signal-attachment:${attachment.id || "unknown"}`;
        }
        payload = createFeedforwardSignalPayload({
          ...pageOptions(config, text, reporter.value.trim(), severity.value, screenshotState, sourceId, annotations, category),
          evidenceRefs: uniqueStrings([...submissionState.evidenceRefs, screenshotState.ref]),
        });
        await patchScreenshot(config, submissionState.signalId, payload);
      }
      status.dataset.tone = "success";
      status.textContent = "Sent.";
      showSuccess();
    } catch (error) {
      status.dataset.tone = "error";
      if (submissionState?.signalId) {
        // The signal is in; only the screenshot leg failed. The button now
        // offers the retry — while uploading it stays on its normal label.
        submit.textContent = "Retry screenshot";
        status.textContent = `Feedforward sent, but screenshot upload failed: ${errorMessage(error)}`;
      } else {
        status.textContent = errorMessage(error);
      }
    } finally {
      submit.disabled = false;
    }
  });

  // Host-provided trigger: the panel opens from the host's own element as a
  // centered modal, and the floating launcher (plus its tone sampling) is off.
  let lastTrigger: HTMLElement | null = null;
  if (config.trigger) {
    root.dataset.mode = "modal";
    panel.setAttribute("aria-modal", "true");
    const onTriggerActivate = (event: Event) => {
      const target = event.target instanceof Element ? event.target : null;
      let match: Element | null = null;
      try {
        match = target?.closest(config.trigger) ?? null;
      } catch {
        return; // invalid selector — never throw into host click handling
      }
      if (!match) return;
      event.preventDefault();
      lastTrigger = match instanceof HTMLElement ? match : null;
      if (!finishSuccess()) setOpen(true);
    };
    // Delegated in the capture phase so SPA re-renders and host
    // stopPropagation can't detach the trigger.
    document.addEventListener("click", onTriggerActivate, true);
    activeTriggerCleanup = () => document.removeEventListener("click", onTriggerActivate, true);
  } else {
    delete root.dataset.mode;
  }

  let toneFrame: number | null = null;
  const observedToneSources = new Set<Element>();
  function applyLauncherTone(): void {
    toneFrame = null;
    const rect = toggle.getBoundingClientRect();
    const sample = backgroundLuminanceAt(
      root,
      rect.left + rect.width / 2,
      rect.top + rect.height / 2,
    );
    if (sample === null) return;
    root.dataset.tone = sample.luminance < 0.45 ? "dark" : "light";
    // Theme flips often land on an app wrapper rather than html/body; watch
    // whichever element actually supplied the background so those re-sample.
    if (
      sample.source !== document.documentElement &&
      sample.source !== document.body &&
      !observedToneSources.has(sample.source)
    ) {
      observedToneSources.add(sample.source);
      toneObserver.observe(sample.source, {
        attributes: true,
        attributeFilter: ["class", "style", "data-theme"],
      });
    }
  }
  function scheduleLauncherTone(): void {
    if (toneFrame !== null) return;
    toneFrame = window.requestAnimationFrame(applyLauncherTone);
  }
  const toneObserver = new MutationObserver(scheduleLauncherTone);
  if (!config.trigger) {
    window.addEventListener("scroll", scheduleLauncherTone, { capture: true, passive: true });
    window.addEventListener("resize", scheduleLauncherTone);
    for (const watched of [document.documentElement, document.body]) {
      if (watched) toneObserver.observe(watched, { attributes: true, attributeFilter: ["class", "style", "data-theme"] });
    }
    const colorScheme =
      typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)") : null;
    if (colorScheme && typeof colorScheme.addEventListener === "function") {
      colorScheme.addEventListener("change", scheduleLauncherTone);
    }
    activeToneCleanup = () => {
      if (toneFrame !== null) window.cancelAnimationFrame(toneFrame);
      toneFrame = null;
      window.removeEventListener("scroll", scheduleLauncherTone, true);
      window.removeEventListener("resize", scheduleLauncherTone);
      toneObserver.disconnect();
      if (colorScheme && typeof colorScheme.removeEventListener === "function") {
        colorScheme.removeEventListener("change", scheduleLauncherTone);
      }
    };
  }

  // Clicking outside the widget dismisses the panel, but only from the menu
  // view — a stray click must not discard a partially written report.
  const onDocumentPointerDown = (event: Event) => {
    if (panel.hidden || menu.hidden) return;
    const target = event.target as Node | null;
    // The root itself is the modal backdrop, so a click landing on it counts
    // as outside; only clicks inside the panel/launcher keep the menu open.
    if (target && target !== root && root.contains(target)) return;
    // If focus lives inside the widget (e.g. on a menu item), hand it back to
    // the launcher so it never strands on a hidden element; otherwise leave
    // the outside click's own focus behavior alone.
    setOpen(false, root.contains(document.activeElement));
  };
  document.addEventListener("pointerdown", onDocumentPointerDown, true);
  activeDismissCleanup = () =>
    document.removeEventListener("pointerdown", onDocumentPointerDown, true);

  root.append(panel, toggle);
  if (!config.trigger) scheduleLauncherTone();
}

function signalBaseUrl(config: WidgetConfig): string {
  const baseUrl = config.baseUrl.replace(/\/$/, "");
  if (config.authMode === "api-key") return `${baseUrl}/public/v1/signals`;
  const resolved = new URL(baseUrl, window.location.href);
  if (resolved.origin !== window.location.origin) {
    throw new Error("Storyloop session mode requires a same-origin base URL.");
  }
  return `${baseUrl}/signals`;
}

function element(tag: string, className: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// Production mark (fully flattened): background, translucent glass, ink.
// Light and dark variants share identical geometry and differ only in the
// background and ink fills, so any background color works (host theming).
const MARK_GLASS_D =
  "M407 476H523.57V491.43C523.57 502.61 526.483 512.853 532.31 522.16C537.763 530.88 545.07 537.687 554.23 542.58C563.39 547.48 573.11 549.773 583.39 549.46C594.37 549.133 604.507 545.863 613.8 539.65L669.13 502.65C670.503 510.517 673.353 517.907 677.68 524.82C683.607 534.287 691.543 541.677 701.49 546.99C711.443 552.31 722.003 554.803 733.17 554.47C745.09 554.11 756.1 550.553 766.2 543.8L838.87 495.22C846.957 504.807 851 516.067 851 529V675C851 689.633 845.827 702.127 835.48 712.48C825.127 722.827 812.633 728 798 728H407C392.367 728 379.873 722.827 369.52 712.48C359.173 702.127 354 689.633 354 675V529C354 514.367 359.173 501.873 369.52 491.52C379.873 481.173 392.367 476 407 476Z";
const MARK_INK_D =
  "M949.101 454.771C954.303 451.293 961.398 453.158 963.84 458.92C973.28 481.24 978 504.6 978 529V675C978 699.4 973.28 722.76 963.84 745.08C954.727 766.62 941.873 785.686 925.28 802.28C908.687 818.873 889.62 831.726 868.08 840.84C845.76 850.28 822.4 855 798 855H451C443.82 855 438 860.82 438 868V921.85C438 933.25 424.37 939.13 416.08 931.3L282.78 805.45C282.26 804.96 281.79 804.45 281.38 803.92C280.827 803.373 280.273 802.826 279.72 802.28C278.11 800.67 276.536 799.037 274.997 797.381C267.84 789.681 273.76 778 284.272 778H798C799.24 778 800.477 777.98 801.71 777.94C814.35 777.5 826.473 774.826 838.08 769.92C849.053 765.28 858.917 758.93 867.67 750.87C868.737 749.883 869.79 748.87 870.83 747.83C871.87 746.79 872.883 745.736 873.87 744.67C881.93 735.916 888.28 726.053 892.92 715.08C897.973 703.14 900.657 690.65 900.97 677.61C900.99 676.743 901 675.873 901 675V529C901 527.76 900.98 526.523 900.94 525.29C900.656 517.147 899.446 509.219 897.31 501.507C895.681 495.627 897.623 489.185 902.695 485.794L949.101 454.771ZM407 349H555.57C562.75 349 568.57 343.179 568.57 336V284.95C568.57 279.756 570.86 275.936 575.44 273.49C580.02 271.043 584.47 271.263 588.79 274.15L743.22 377.38C747.067 379.953 748.99 383.556 748.99 388.19C748.99 392.823 747.067 396.426 743.22 399L588.79 502.24C584.47 505.126 580.02 505.343 575.44 502.89C570.86 500.443 568.57 496.623 568.57 491.43V439C568.57 431.82 562.75 426 555.57 426H407C406.127 426 405.253 426.01 404.38 426.03C391.347 426.343 378.86 429.026 366.92 434.08C355.58 438.873 345.427 445.496 336.46 453.95C335.687 454.676 334.923 455.416 334.17 456.17C333.423 456.91 332.693 457.66 331.98 458.42C323.513 467.4 316.88 477.566 312.08 488.92C307.167 500.526 304.493 512.656 304.06 525.31C304.02 526.536 304 527.766 304 529V675C304 676.113 304.017 677.223 304.05 678.33C304.113 680.38 304.236 682.417 304.417 684.44C305.175 692.919 298.976 701 290.464 701H240.105C233.64 701 228.106 696.251 227.589 689.806C227.196 684.914 227 679.978 227 675V529C227 504.6 231.72 481.24 241.16 458.92C250.273 437.38 263.127 418.313 279.72 401.72C296.313 385.126 315.38 372.273 336.92 363.16C359.24 353.72 382.6 349 407 349ZM892.83 377.38C896.683 379.96 898.61 383.563 898.61 388.19C898.61 392.823 896.683 396.426 892.83 399L738.41 502.24C734.09 505.126 729.64 505.343 725.06 502.89C720.473 500.443 718.18 496.623 718.18 491.43V450.347C718.18 446.006 720.347 441.951 723.956 439.539L756 418.12C761.18 414.66 765.177 410.2 767.99 404.74C770.657 399.566 771.99 394.05 771.99 388.19C771.99 382.33 770.657 376.813 767.99 371.64C765.177 366.186 761.18 361.726 756 358.26L723.956 336.84C720.347 334.428 718.18 330.374 718.18 326.033V284.95C718.18 279.756 720.473 275.936 725.06 273.49C729.64 271.043 734.09 271.263 738.41 274.15L892.83 377.38Z";

// Transparent variant: no background path. The button's own background is the
// reactive outer fill (tone flips it, buttonColor themes it), and the ink
// follows the button's CSS color via currentColor.
function foreloopMark(): SVGSVGElement {
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("class", "slfb-foreloop-mark");
  svg.setAttribute("viewBox", "0 0 1200 1200");
  svg.setAttribute("fill", "none");
  svg.setAttribute("aria-hidden", "true");
  const shapes: Array<[d: string, attrs: Record<string, string>]> = [
    [MARK_GLASS_D, { fill: "#AEAEAE", "fill-opacity": "0.5" }],
    [MARK_INK_D, { fill: "currentColor" }],
  ];
  for (const [d, attrs] of shapes) {
    const node = document.createElementNS(NS, "path");
    node.setAttribute("d", d);
    for (const [key, val] of Object.entries(attrs)) node.setAttribute(key, val);
    svg.append(node);
  }
  return svg;
}

function parseCssColor(value: string): { r: number; g: number; b: number; a: number } | null {
  const raw = value.trim().toLowerCase();
  if (raw === "transparent") return { r: 0, g: 0, b: 0, a: 0 };
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(raw)?.[1];
  if (hex) {
    const digits = hex.length === 3 ? [...hex].map((d) => d + d) : hex.match(/../g) ?? [];
    const [r = 0, g = 0, b = 0] = digits.map((pair) => parseInt(pair, 16));
    return { r, g, b, a: 1 };
  }
  const body = /^rgba?\(([^)]+)\)$/.exec(raw)?.[1];
  if (!body) return null;
  const parts = body.split(/[\s,/]+/).filter(Boolean).map((part) => parseFloat(part));
  if (parts.length < 3 || parts.some((part) => Number.isNaN(part))) return null;
  const [r = 0, g = 0, b = 0, a = 1] = parts;
  return { r, g, b, a };
}

function relativeLuminance(color: { r: number; g: number; b: number }): number {
  return (0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b) / 255;
}

// Luminance of a gradient near the launcher, or null when the value has no
// gradient (plain url() images stay unknowable without pixel readback). The
// launcher anchors to the bottom-right, so the final color stop — the end of
// default top-down and diagonal flows — is the best cheap approximation of
// the surface actually behind it.
function gradientLuminance(backgroundImage: string): number | null {
  if (!backgroundImage || !backgroundImage.includes("gradient(")) return null;
  const stops = backgroundImage.match(/rgba?\([^)]+\)|#[0-9a-f]{3,8}/gi);
  if (!stops) return null;
  for (let index = stops.length - 1; index >= 0; index -= 1) {
    const color = parseCssColor(stops[index] ?? "");
    if (color && color.a >= 0.5) return relativeLuminance(color);
  }
  return null;
}

// Relative luminance (0..1) of the page surface under (x, y) plus the element
// that supplied it, ignoring the widget's own elements. Returns null when no
// opaque background is found.
function backgroundLuminanceAt(
  root: HTMLElement,
  x: number,
  y: number,
): { luminance: number; source: Element } | null {
  let stack: Element[] = [];
  try {
    if (typeof document.elementsFromPoint === "function") {
      stack = document.elementsFromPoint(x, y);
    }
  } catch {
    stack = [];
  }
  for (const fallback of [document.body, document.documentElement]) {
    if (fallback && !stack.includes(fallback)) stack.push(fallback);
  }
  for (const node of stack) {
    if (!node || root.contains(node)) continue;
    const style = window.getComputedStyle(node);
    const color = parseCssColor(style.backgroundColor);
    if (color && color.a >= 0.5) return { luminance: relativeLuminance(color), source: node };
    const gradient = gradientLuminance(style.backgroundImage);
    if (gradient !== null) return { luminance: gradient, source: node };
  }
  return null;
}

function menuIcon(paths: string[]): SVGSVGElement {
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("class", "slfb-menu-icon");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  for (const d of paths) {
    const path = document.createElementNS(NS, "path");
    path.setAttribute("d", d);
    svg.append(path);
  }
  return svg;
}

function labeled(labelText: string | Node, control: HTMLElement): HTMLLabelElement {
  const label = document.createElement("label");
  label.className = "slfb-label";
  label.append(labelText, control);
  return label;
}

function init(options: WidgetOptions = {}): void {
  installStyles(value(options, "styleNonce"));
  let root = document.getElementById(ROOT_ID);
  if (!root) {
    root = document.createElement("div");
    root.id = ROOT_ID;
    root.className = "slfb-root";
    document.body.appendChild(root);
  }
  createPanel(configFrom(options), root);
}

const widgetApi = window.StoryloopFeedforward ?? window.StoryloopFeedback ?? { init };
widgetApi.init = init;
window.StoryloopFeedforward = widgetApi;
window.StoryloopFeedback = widgetApi;

ready(() => {
  if (dataset().autoInit !== "false") init();
});
